/**
 * AIOE (D378, the organisation's half of "Routines started by events"): the services that forward signed events to the
 * organisation, which published routines each may start, and what the organisation did with each event. The routine's own
 * triggers, the `routine.trigger` order and its answers are the workbench's (./routineTriggers.ts); this file is the
 * platform's and the workbench never mirrors it.
 *
 * Routes (docs/workbench-integration.md, section "Routines started by events (D378)"):
 *
 *   GET    /admin/v1/routine-events                     administrators → {@link RoutineEventsResponse}: the services, the newest
 *                                                       events and the routines a rule may name
 *   POST   /admin/v1/routine-events/sources             {@link RoutineEventSourceCreate} → 201 {@link RoutineEventSourceCreated}:
 *                                                       the address and, when the platform made it, the secret, shown once
 *   PATCH  /admin/v1/routine-events/sources/:id         {@link RoutineEventSourcePatch} → {@link RoutineEventSourceView}
 *   POST   /admin/v1/routine-events/sources/:id/secret  { secret? } → {@link RoutineEventSourceCreated}: a pasted secret is kept,
 *                                                       none makes a new one (shown once)
 *   DELETE /admin/v1/routine-events/sources/:id         → 204, with its events
 *   GET    /admin/v1/routine-events/log?source=&limit=  → { events: {@link ForwardedEventView}[] }, newest first
 *   POST   /webhooks/v1/:sourceId                       public: the service's own signature admits it (see
 *                                                       {@link ROUTINE_EVENT_SERVICES}); 200 { ok, started }, 401 `bad_signature`
 *                                                       or `stale`, 409 `replayed`, 429 `rate_limited`, 404 `not_found`
 *
 * A source's signing secret is sealed at rest (secrets.ts) and is in no answer but the one that made it.
 *
 * Words for people: "When something happens", "your organisation forwards an event", the service's own name. Never
 * "trigger", "webhook" or "event source" on screen, except in the steps that say where to paste the address in the
 * other service.
 *
 * Pure: zod and string helpers only.
 */
import { z } from "zod";
import { OUTSIDE_EVENT_SOURCES } from "./routineTriggers.js";
/** Where a service posts: `${publicUrl}/webhooks/v1/<source id>`. */
export const ROUTINE_EVENT_INTAKE_PATH = "/webhooks/v1";
/** The newest events kept per source, and for how long at most. */
export const FORWARDED_EVENTS_KEEP = 500;
export const FORWARDED_EVENTS_KEEP_DAYS = 30;
/** Accepted events a source may forward in a minute; more answer 429 with retry-after. */
export const ROUTINE_EVENTS_PER_MINUTE = 120;
/** Machines one event may reach for one routine. */
export const ROUTINE_EVENT_MAX_MACHINES = 25;
/**
 * A `routine.trigger` order nobody answered is dropped after this long: an event is news for a while, not for a month.
 * Twelve hours, so a desktop asleep overnight still runs it when it wakes (the run says when the event happened).
 */
export const ROUTINE_TRIGGER_ORDER_TTL_MS = 12 * 3600 * 1000;
/** A delivery that names its own time (Linear, Sentry) is refused when it is further than this from now. */
export const ROUTINE_EVENT_MAX_SKEW_MS = 5 * 60 * 1000;
export const ROUTINE_EVENT_SERVICES = {
    github: {
        id: "github",
        name: "GitHub",
        secretFromService: false,
        subjectWords: "Repositories",
        subjectExample: "mojoup/shop or mojoup/*",
        steps: [
            "In GitHub, open the organisation's or the repository's Settings, then Webhooks, and choose Add webhook.",
            "Paste the address below as the Payload URL and choose application/json as the content type.",
            "Paste the secret below as the Secret.",
            "Choose the events to send (for example Workflow runs, Issues and Pull requests), then Add webhook.",
        ],
        kinds: [
            { kind: "workflow_run.completed:failure", words: "A workflow run fails" },
            { kind: "workflow_run.completed", words: "A workflow run finishes" },
            { kind: "issues.labeled", words: "An issue is labelled" },
            { kind: "issues.opened", words: "An issue is opened" },
            { kind: "pull_request.opened", words: "A pull request is opened" },
            { kind: "pull_request.review_requested", words: "A pull request asks for review" },
            { kind: "pull_request_review.submitted:changes_requested", words: "A pull request gets changes requested" },
            { kind: "release.published", words: "A release is published" },
            { kind: "push", words: "Commits are pushed" },
        ],
    },
    "azure-devops": {
        id: "azure-devops",
        name: "Azure DevOps",
        secretFromService: false,
        subjectWords: "Projects or repositories",
        subjectExample: "Shop or shop-api",
        steps: [
            "In Azure DevOps, open Project settings, then Service hooks, and create a subscription with Web Hooks for each kind of event.",
            "Paste the address below as the URL.",
            "Under Basic authentication, enter any user name and the secret below as the password (or add the HTTP header X-AIOE-Webhook-Secret with the secret).",
            "Leave the resource details to send as All, then Finish.",
        ],
        kinds: [
            { kind: "build.complete:failed", words: "A build fails" },
            { kind: "build.complete", words: "A build finishes" },
            { kind: "ms.vss-pipelines.run-state-changed-event:failed", words: "A pipeline run fails" },
            { kind: "git.pullrequest.created", words: "A pull request is created" },
            { kind: "git.pullrequest.updated", words: "A pull request is updated" },
            { kind: "workitem.created", words: "A work item is created" },
            { kind: "workitem.updated", words: "A work item is updated" },
            { kind: "git.push", words: "Code is pushed" },
        ],
    },
    linear: {
        id: "linear",
        name: "Linear",
        secretFromService: true,
        subjectWords: "Teams",
        subjectExample: "ENG",
        steps: [
            "In Linear, open Settings, then API, and create a new webhook.",
            "Paste the address below as the URL and choose what to send (for example Issues and Comments), then save.",
            "Copy the signing secret Linear shows and paste it here.",
        ],
        kinds: [
            { kind: "issue.create", words: "An issue is created" },
            { kind: "issue.update", words: "An issue is updated" },
            { kind: "issue.update:label:*", words: "An issue is labelled" },
            { kind: "comment.create", words: "Someone comments" },
            { kind: "project.update", words: "A project is updated" },
        ],
    },
    sentry: {
        id: "sentry",
        name: "Sentry",
        secretFromService: true,
        subjectWords: "Projects",
        subjectExample: "checkout",
        steps: [
            "In Sentry, open Settings, then Developer Settings, and create an internal integration.",
            "Paste the address below as the Webhook URL, turn on Alert Rule Action and the Issue and Error webhooks you want, then save.",
            "Copy the Client Secret Sentry shows and paste it here.",
        ],
        kinds: [
            { kind: "issue.created", words: "A new issue appears" },
            { kind: "event_alert.triggered", words: "An alert rule fires" },
            { kind: "metric_alert.critical", words: "A metric alert turns critical" },
            { kind: "issue.resolved", words: "An issue is resolved" },
            { kind: "error.created", words: "An error is reported" },
        ],
    },
    webhook: {
        id: "webhook",
        name: "Another service",
        secretFromService: false,
        subjectWords: "Subjects",
        subjectExample: "billing",
        steps: [
            "In the other service, set up a webhook that POSTs JSON to the address below.",
            "Sign each request with the header X-Signature-256: sha256=<the HMAC-SHA256 of the raw body with the secret below, in hex>.",
            "Optionally send X-Event-Kind (what happened), X-Delivery-Id (so a repeat is refused), and in the body summary, url and subject.",
        ],
        kinds: [],
    },
};
const pattern = z.string().trim().min(1).max(200);
/**
 * One routine (or, AIOE 0.90.0, one workflow) a source may start, and when: only events about these subjects, of these kinds
 * (`*` matches anything). A rule names exactly one of `routineId` and `workflowId`.
 */
export const routineEventRuleSchema = z
    .object({
    projectId: z.string().trim().min(1).max(100),
    /** The published routine's id in the project's catalogue (D363): the event starts it with `routine.trigger`. */
    routineId: z.string().regex(/^[a-z0-9][a-z0-9-]{0,40}$/, "a routine's id is lower-case letters, digits and hyphens").optional(),
    /** D401: a published workflow's id in the project's catalogue instead: the event starts it with `workflow.start`, the event as `run.event`. */
    workflowId: z.string().regex(/^[a-z0-9][a-z0-9-]{0,40}$/, "a workflow's id is lower-case letters, digits and hyphens").optional(),
    /** Repositories, projects or teams, as {@link RoutineEventService.subjectWords} says. Empty: any. */
    only: z.array(pattern).max(20).optional(),
    /** Kinds of event, as the service names them (`issues.labeled`), or with what it says about them (`workflow_run.completed:failure`). Empty: any. */
    kinds: z.array(z.string().trim().min(1).max(120)).max(20).optional(),
    /**
     * D447, workflows only: also start the workflow on a timetable (a five-field cron and a time zone). AIOE keeps it; it
     * starts the workflow (or routine) when it comes round, in the time zone, once (the `routine-events.schedules` job).
     */
    schedule: z
        .object({
        /** Five fields, such as `0 9 * * 1-5`; the platform checks it and answers 400 `bad_schedule`. */
        cron: z.string().trim().min(1).max(100),
        timeZone: z.string().trim().min(1).max(64),
    })
        .strict()
        .optional(),
    /**
     * D447, GitHub only: the GitHub Actions workflow files (`.github/workflows/agents.yml`, `*` matches any run) whose runs may
     * join a node for this rule's project with their own OIDC token (POST /compute/v1/github/join), from the repositories in
     * `only`. Without it the rule admits no run.
     */
    githubWorkflowFiles: z.array(z.string().trim().min(1).max(200)).max(20).optional(),
})
    .strict()
    .refine((r) => (r.routineId === undefined) !== (r.workflowId === undefined), { message: "a rule starts one routine or one workflow: name one of them", path: ["routineId"] })
    .refine((r) => r.workflowId !== undefined || (r.schedule === undefined && r.githubWorkflowFiles === undefined), { message: "a timetable and GitHub workflow files go with a workflow", path: ["schedule"] });
const sourceName = z.string().trim().min(1, "give it a name people recognise").max(80);
/** A secret pasted from the other service. */
const pastedSecret = z.string().trim().min(16, "the secret is at least 16 characters").max(300);
export const routineEventSourceCreateSchema = z
    .object({
    service: z.enum(OUTSIDE_EVENT_SOURCES),
    name: sourceName,
    rules: z.array(routineEventRuleSchema).max(20).default([]),
    /** The secret the other service made (Linear, Sentry); absent, the platform makes one and answers it once. */
    secret: pastedSecret.optional(),
})
    .strict();
export const routineEventSourcePatchSchema = z
    .object({ name: sourceName.optional(), enabled: z.boolean().optional(), rules: z.array(routineEventRuleSchema).max(20).optional() })
    .strict();
export const routineEventSecretSchema = z.object({ secret: pastedSecret.optional() }).strict();
/** Why a delivery was not taken, kept on the source so its card can say so. */
export const ROUTINE_EVENT_REFUSALS = ["bad_signature", "no_secret", "replayed", "stale", "rate_limited", "unreadable", "off"];
export const ROUTINE_EVENT_REFUSAL_WORDS = {
    bad_signature: "its signature did not match the secret",
    no_secret: "no secret has been pasted yet",
    replayed: "it had already arrived once",
    stale: "it was sent too long ago",
    rate_limited: "too many arrived in a minute",
    unreadable: "it was not JSON the platform could read",
    off: "it arrived while this was turned off",
};
// ── Matching ───────────────────────────────────────────────────────────
/** `*` matches any run of characters; the rest is literal, whatever its case. */
export function eventPatternMatches(pattern, value) {
    const re = pattern
        .trim()
        .split("*")
        .map((s) => s.replace(/[.+?^${}()|[\]\\]/g, "\\$&"))
        .join(".*");
    return new RegExp(`^${re}$`, "i").test(value);
}
/**
 * Whether a rule takes this event: its subjects (any of them) fit one of the rule's `only`, and its kind or one of its
 * facets (`workflow_run.completed:failure`, `issues.labeled:bug`) fits one of the rule's `kinds`. An empty list takes all.
 */
export function routineEventRuleMatches(rule, event) {
    if (rule.only?.length && !event.subjects.some((s) => rule.only.some((p) => eventPatternMatches(p, s))))
        return false;
    const kinds = [event.kind, ...event.facets];
    if (rule.kinds?.length && !kinds.some((k) => rule.kinds.some((p) => eventPatternMatches(p, k))))
        return false;
    return true;
}
/** A kind as people read it: the service's offered words when it is one of them, else the kind itself. */
export function routineEventKindWords(service, kind) {
    return ROUTINE_EVENT_SERVICES[service].kinds.find((k) => k.kind === kind)?.words ?? kind;
}
/** A machine's answer in words for the log: "Started", "Did not hold it", "Turned down: …". */
export function forwardedMachineWords(m) {
    if (m.state === "pending")
        return "Waiting for the machine";
    if (m.state === "expired")
        return "The machine did not answer in time";
    if (m.state === "done")
        return m.message || "Started";
    if (m.code === "no_routine")
        return "Does not have this routine, or it does not start on forwarded events";
    if (m.code === "no_workflow")
        return "Does not have this workflow";
    if (m.code === "no_member" || m.code === "invalid_inputs")
        return m.message || "Could not start the workflow";
    if (m.code === "rate_limited")
        return m.message || "Already started as often as it may this hour";
    if (m.code === "not_allowed" || m.code === "outside_not_allowed")
        return m.message || "The workspace policy does not let it start on an event";
    return m.message || (m.state === "failed" ? "Failed" : "Turned down");
}
//# sourceMappingURL=routineEventSources.js.map